import { useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useNavigate } from 'react-router-dom';
import { ProposalResponseViewModel } from 'types/proposal';
import getTransformedDate from 'utils/getTransformedDate';
import { AppointmentDetailsRow } from './AppointmentDetailsRow';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import numberWithCommas from 'utils/numberWithCommas';
import { parametrizeURL } from 'routes/utils';
import { PATH } from 'routes/path';
import Divider from '@mui/material/Divider';
import { IconButton, Tooltip } from '@mui/material';
import { BackdropLoader } from 'components/@shared/Loaders';
import { generateEmailEstimateNewInvoice } from 'services/invoiceService';
import isNull from 'lodash/isNull';

interface Props {
    appointmentId: string;
    proposals: ProposalResponseViewModel[];
    showActions: boolean;
    onProposalConfirm: (id: string, isConfirmed: boolean) => void;
}

const proposalStyles = {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'primary.light',
    borderRadius: '4px',
    gap: 2,
    p: 2,
};

export const AppointmentProposals = ({ appointmentId, proposals, showActions, onProposalConfirm }: Props) => {
    const [isLoading, setIsLoading] = useState(false);

    const navigate = useNavigate();

    const createProposalHandler = useCallback(() => {
        navigate(parametrizeURL(PATH.CREATE_PROPOSAL, { appointmentId }));
    }, [navigate]);

    const editProposalHandler = useCallback(
        (id: string) => {
            navigate(parametrizeURL(PATH.EDIT_PROPOSAL, { id }));
        },
        [navigate],
    );

    return (
        <Box>
            {isLoading && <BackdropLoader />}
            {showActions && (
                <Box display="flex" alignItems="center" my={2} gap={1} justifyContent="flex-end">
                    <Button variant="outlined" onClick={createProposalHandler}>
                        Add New Proposal
                    </Button>
                </Box>
            )}
            {proposals.length ? (
                <Stack sx={proposalStyles}>
                    {proposals.map((proposal) => (
                        <Box key={proposal.id}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="h5" noWrap>
                                    {proposal.title}
                                </Typography>
                                {proposal.isCustomerConfirmed ? (
                                    <Stack direction="row" gap={1} alignItems="center" justifyContent="flex-end">
                                        <CheckCircle color="success" />
                                        <Typography variant="body2" noWrap>
                                            Confirmed
                                        </Typography>
                                        <Box pl={1}>
                                            <Button
                                                variant="contained"
                                                color="error"
                                                onClick={() => onProposalConfirm(proposal.id, false)}
                                            >
                                                Decline
                                            </Button>
                                        </Box>
                                    </Stack>
                                ) : (
                                    <Stack
                                        direction="row"
                                        gap={0.5}
                                        py={1}
                                        alignItems="center"
                                        justifyContent="flex-end"
                                    >
                                        {showActions && (
                                            <Tooltip title="Edit Proposal" placement="top">
                                                <IconButton onClick={() => editProposalHandler(proposal.id)}>
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <ErrorOutlineIcon color="warning" />
                                        <Typography variant="body2" noWrap>
                                            Not Confirmed
                                        </Typography>
                                        {showActions && (
                                            <Box pl={1}>
                                                <Button
                                                    variant="contained"
                                                    onClick={() => onProposalConfirm(proposal.id, true)}
                                                >
                                                    Confirm
                                                </Button>
                                            </Box>
                                        )}
                                    </Stack>
                                )}
                            </Box>
                            <Divider />
                            <Stack gap={2} py={2}>
                                <AppointmentDetailsRow
                                    title="Total"
                                    description={`$${numberWithCommas(proposal.total)}`}
                                />
                                <AppointmentDetailsRow
                                    title="Expired"
                                    description={getTransformedDate(proposal.expirationDate)}
                                />
                                <Stack gap={1}>
                                    <Typography fontWeight={500} mb={0.5}>
                                        Services:
                                    </Typography>
                                    {proposal.lineItems.map((item) => (
                                        <Box key={item.id}>
                                            <Divider />
                                            <Typography variant="body2" my={0.5}>
                                                name: {item.name}
                                            </Typography>
                                            <Typography variant="body2" my={0.5}>
                                                description: {item.description}
                                            </Typography>
                                            <Typography variant="body2" my={0.5}>
                                                price: ${numberWithCommas(item.total, 2)}
                                            </Typography>
                                            <Divider />
                                        </Box>
                                    ))}
                                </Stack>
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            ) : (
                <Typography my={1}>no proposals</Typography>
            )}
        </Box>
    );
};
